import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { apiGet } from '@/api/client';
import { AsyncSection } from '@/app/components/AsyncSection';
import { Button } from '@/app/components/ui/Button';
import { DataTable } from '@/app/components/ui/DataTable';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useMe } from '@/features/me/MeContext';
import { useAsync } from '@/lib/useAsync';

import { CompaniesHealthSummaryCard } from './CompaniesHealthSummaryCard';

/**
 * The companies visible to the caller — the API scopes the list itself:
 * everything for an admin, otherwise memberships and administered companies.
 */
export function CompaniesPage() {
  const { t } = useTranslation();
  const { permissions } = useMe();
  const companies = useAsync(() => apiGet('/companies'), []);

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader
        title={t('companies.list.title')}
        actions={
          permissions.canCreateCompany ? (
            <Button as={Link} to="/manage/companies/new">
              {t('companies.list.newCompany')}
            </Button>
          ) : undefined
        }
      />

      <AsyncSection state={companies} label={t('companies.list.rowsLabel')} skeleton="table">
        {(rows) =>
          rows.length === 0 ? (
            <EmptyState text={t('companies.list.empty')} />
          ) : (
            <>
              <CompaniesHealthSummaryCard companies={rows} />
              <DataTable
                rowKey={(company) => company.id ?? ''}
                rows={rows}
                columns={[
                  {
                    key: 'name',
                    header: t('companies.list.name'),
                    render: (company) => (
                      <Link to={`/manage/companies/${company.id}`}>{company.name}</Link>
                    ),
                  },
                  {
                    key: 'created',
                    header: t('companies.list.created'),
                    render: (company) => company.created_at,
                  },
                ]}
              />
            </>
          )
        }
      </AsyncSection>
    </section>
  );
}
